import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ClarifyingQuestions({ questions, onSubmit, submitting }) {
  const [answers, setAnswers] = useState(() =>
    questions.map((q) => ({
      question: q.question,
      answer: "",
      _customValue: "",
    }))
  );

  const setAnswer = (idx, value) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], answer: value };
      return next;
    });
  };

  const setCustom = (idx, value) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], _customValue: value, answer: value };
      return next;
    });
  };

  const allAnswered = answers.every((a) => (a.answer || "").trim().length > 0);

  const handleSubmit = () => {
    if (!allAnswered) return;
    onSubmit(
      answers.map((a) => ({ question: a.question, answer: a.answer.trim() }))
    );
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#35D07F]">
          Step 2 of 3
        </div>
        <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-white">
          A few quick clarifications
        </h2>
        <p className="mt-2 text-sm text-[#A0AAB2] max-w-2xl leading-relaxed">
          The sharper these are, the sharper your questions get. Pick an option
          or type your own.
        </p>
      </div>

      <div className="space-y-6">
        {questions.map((q, idx) => (
          <div
            key={idx}
            data-testid={`clarify-${idx}`}
            className="rounded-2xl border border-white/10 bg-[#12181A] p-6"
          >
            <div className="flex items-baseline gap-3">
              <span className="text-xs font-mono text-[#35D07F]">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <div className="text-white font-medium leading-snug">
                {q.question}
              </div>
            </div>

            {q.options && q.options.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {q.options.map((opt, oi) => {
                  const selected = answers[idx]?.answer === opt;
                  return (
                    <button
                      key={oi}
                      type="button"
                      onClick={() => setAnswer(idx, opt)}
                      data-testid={`clarify-${idx}-option-${oi}`}
                      className={`rounded-full px-4 py-2 text-sm border transition-[background-color,border-color,color] duration-150 ${
                        selected
                          ? "bg-[#35D07F] text-black border-[#35D07F]"
                          : "bg-transparent text-white/90 border-white/15 hover:border-white/40"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
                <div className="basis-full h-0" />
                <Input
                  value={answers[idx]?._customValue || ""}
                  onChange={(e) => setCustom(idx, e.target.value)}
                  placeholder="…or type your own"
                  data-testid={`clarify-${idx}-custom`}
                  className="mt-1 bg-[#0B0F10] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#35D07F]/50"
                />
              </div>
            ) : (
              <Input
                value={answers[idx]?.answer || ""}
                onChange={(e) => setAnswer(idx, e.target.value)}
                placeholder="Type your answer"
                data-testid={`clarify-${idx}-input`}
                className="mt-4 bg-[#0B0F10] border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#35D07F]/50"
              />
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-4">
        <Button
          onClick={handleSubmit}
          disabled={!allAnswered || submitting}
          data-testid="clarify-submit"
          className="rounded-full bg-[#35D07F] hover:bg-[#2CBB70] text-black font-semibold px-6 h-11 disabled:opacity-40"
        >
          {submitting ? "Running…" : "Run the engine"}
        </Button>
        {!allAnswered && (
          <span className="text-xs text-[#A0AAB2]">
            Answer every question to continue.
          </span>
        )}
      </div>
    </div>
  );
}
